import { useQuery } from "@tanstack/react-query";
import { Activity, Database, Radio, AlertTriangle } from "lucide-react";

import { AdminAPI } from "@/lib/admin-api";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Skeleton } from "@/components/ui/Skeleton";
import { StatusPill } from "@/components/ui/StatusPill";
import { MetricCard } from "./components/MetricCard";

export function SystemHealthPage() {
  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["admin-health"],
    queryFn: AdminAPI.health,
    refetchInterval: 10_000,
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap justify-between items-start gap-4">
        <div>
          <h1 className="text-2xl font-bold text-surface-900">System Health</h1>
          <p className="text-sm text-surface-500 mt-1">
            Live infrastructure metrics · refreshes every 10 seconds
          </p>
        </div>
        <div className="text-2xs text-surface-500 flex items-center gap-1">
          <div className={`w-2 h-2 rounded-full ${isFetching ? "bg-warning-500 animate-pulse" : "bg-success-500"}`} />
          {isFetching ? "Refreshing…" : "Live"}
        </div>
      </div>

      {isLoading || !data ? (
        <div className="grid gap-4 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-32 rounded-xl" />)}
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-3">
          <MetricCard icon={Database} label="Database"
                      tone={data.database === "ok" ? "success" : "danger"}
                      value={<StatusPill status={data.database === "ok" ? "delivered" : "failed"} />}
                      hint="PostgreSQL primary" />
          <MetricCard icon={Radio} label="Outbox Pending"
                      tone={data.outbox_pending > 100 ? "warning" : "info"}
                      value={data.outbox_pending}
                      hint="Awaiting dispatch" />
          <MetricCard icon={AlertTriangle} label="Outbox DLQ"
                      tone={data.outbox_dlq > 0 ? "danger" : "success"}
                      value={data.outbox_dlq}
                      hint={data.outbox_dlq > 0 ? "Requires investigation" : "All clear"} />
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Infrastructure notes</CardTitle>
          <Activity className="h-4 w-4 text-surface-400" />
        </CardHeader>
        <CardBody className="space-y-3 text-sm text-surface-700">
          <Section title="Modular Monolith Architecture">
            All 20 engines share a single PostgreSQL database with strict logical ownership.
            No engine directly writes another engine's tables — all cross-engine mutations flow through Domain Events
            or explicit service interfaces.
          </Section>
          <Section title="Transactional Outbox + Inbox">
            Guarantees exactly-once event delivery: state changes and event emission commit atomically;
            subscribers dedupe via the Inbox table. Dispatcher preserves per-Entry ordering via partition keys.
          </Section>
          <Section title="Version-Pinned Configuration">
            Every Club Entry pins its configuration version at creation. Config changes never
            retroactively affect running Entries.
          </Section>
          <Section title="Immutable Audit & Ledger">
            All financial and business records are append-only. Corrections are additive
            (adjustment / reversal), never destructive.
          </Section>
        </CardBody>
      </Card>
    </div>
  );
}

function Section({ title, children }: React.PropsWithChildren<{ title: string }>) {
  return (
    <div className="border-l-2 border-brand-500 pl-4">
      <div className="font-semibold text-surface-900">{title}</div>
      <p className="text-surface-600 mt-1 leading-relaxed">{children}</p>
    </div>
  );
}
